/* Copyright (c) 2020-present CloudIO Inc */

/* eslint-disable no-new-func */
/* eslint-disable @typescript-eslint/no-explicit-any */

import * as dsTypes from '@cloudio-saas/datasource-types';
import * as Mutx from '@cloudio-saas/mutx';
import * as StateX from '@cloudio-saas/statex';
import * as ChartJS from 'chart.js';
import * as MUILab from '../mui-lab';
import * as MUI from '@mui/material';
import * as MUIStyles from '@mui/styles';
import clsx from 'clsx';
import {
  ComponentItemType,
  ComponentManager,
  ComponentManagerFn,
  deregisterCustomComponents,
  registerManager,
  registerParentItemTypes,
  unknownManager,
} from 'componentregistry';
import * as ReactDropZone from 'react-dropzone';
import axios from 'axios';
import type { Platform } from 'componentregistry/platform/Platform';
import UnknownComponent from 'componentregistry/registry/UnknownComponent';
import Controller from 'controller/Controller';
import { getController } from 'controller/ControllerRegistry';
import {
  add,
  addDays,
  differenceInDays,
  endOfDay,
  format,
  formatDistanceToNow,
  isAfter,
  isBefore,
  isDate,
  isFriday,
  isFuture,
  isMonday,
  isPast,
  isSameDay,
  isSaturday,
  isSunday,
  isThursday,
  isToday,
  isTuesday,
  isValid,
  isWednesday,
  isWeekend,
  parseISO,
  startOfDay,
  sub,
  subDays,
  subMonths,
} from 'date-fns';
import { hmrAtom } from 'designer/atoms';
import { AnimatePresence, motion } from 'framer-motion';
import { camelCase, upperFirst } from 'lodash-es';
import * as React from 'react';
import { registerComponent } from 'registry/Components';
import { ulid } from 'ulid';
import { globalCache } from 'utils/cache';
import { getErrorMessage } from 'utils/isEmpty';
import * as ReactWindow from 'react-window';

// workaround for Cannot access '__WEBPACK_DEFAULT_EXPORT__' before initialization
const cloudioImport = import('../cloudio');
export interface InstalledComponent {
  name: string;
  manager: string;
  component: string;
  version: string;
  componentId: string;
  appUid: string;
  sourceId: string;
}

export interface CompiledInstalledComponent {
  ic: dsTypes.IoInstalledComponentsV;
  mgr: ComponentManager<any, any>;
  comp: React.FunctionComponent<any>;
}

const InstalledComponentsMap = globalCache.get<
  Record<string, CompiledInstalledComponent[]>
>('InstalledComponentsMap', () => ({}));

const getRequireBase =
  ({
    platform,
    appUid = 'cloudio',
    cloudio,
  }: {
    platform: Platform;
    appUid?: string;
    cloudio: any;
  }) =>
  (moduleName: string) => {
    switch (moduleName) {
      case 'platform':
        return platform;
      case 'react':
        return React;
      case '@mui/material':
        return MUI;
      case '@mui/styles':
        return MUIStyles;
      case 'react-dropzone':
        return ReactDropZone;
      case 'chart.js':
        return ChartJS;
      case 'axios':
        return axios;
      case '@mui/lab':
        return MUILab;
      case '@cloudio-saas/datasource-types':
        return dsTypes;
      case '@cloudio-saas/statex':
        return StateX;
      case '@cloudio-saas/mutx':
        return Mutx;
      case 'uuid':
      case 'ulid':
        return ulid;
      case 'clsx':
        return clsx;
      case 'cloudio':
        return cloudio;
      case 'framer-motion':
        return { motion, AnimatePresence };
      case 'date-fns':
        return {
          add,
          addDays,
          differenceInDays,
          endOfDay,
          format,
          formatDistanceToNow,
          isAfter,
          isBefore,
          isDate,
          isFriday,
          isFuture,
          isMonday,
          isPast,
          isSameDay,
          isSaturday,
          isSunday,
          isThursday,
          isToday,
          isTuesday,
          isValid,
          isWednesday,
          isWeekend,
          parseISO,
          startOfDay,
          sub,
          subDays,
          subMonths,
        };
      case 'react-window':
        return ReactWindow;
      case 'app':
        return getController(appUid, 'AppController');
      default:
        throw Error(`You can't use module ${moduleName} in the controller.`);
    }
  };

async function compile(
  platform: Platform,
  ic: dsTypes.IoInstalledComponentsV,
): Promise<CompiledInstalledComponent | null> {
  const { name, managerJs, componentJs, version, componentUid, appUid } = ic;
  const componentTypeName = upperFirst(camelCase(name));
  try {
    const exports: { default?: any } = {};
    const module = { exports };
    const cloudio = await cloudioImport;
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const require = getRequireBase({ platform, cloudio });
    let result = await Controller.transform(
      `${componentTypeName}Manager.tsx`,
      managerJs,
      platform.isDeveloper(appUid),
    );
    let mgr: ComponentManager<any, any> | null = null;
    if (result.code) {
      // eslint-disable-next-line no-eval
      eval(result.code);
      if (module.exports.default) {
        const mgrFn: ComponentManagerFn<any, any> = module.exports.default;
        try {
          mgr = mgrFn(platform, componentTypeName);
          mgr.id = componentUid;
          mgr.version = version;
        } catch (e) {
          throw Error(`Error calling ${name} Manager: ${getErrorMessage(e)}`);
        }
      } else {
        throw Error(`Missing default export in ${name} Manager!`);
      }
    } else {
      throw Error(`Error compiling ${name} Manager: ${result.error}`);
    }
    result = await Controller.transform(
      `${componentTypeName}Component.tsx`,
      componentJs,
      platform.isDeveloper(appUid),
    );
    let comp: React.FunctionComponent<any> | null = null;
    if (result.code) {
      // eslint-disable-next-line no-eval
      eval(result.code);
      comp = module.exports.default;
    } else {
      throw Error(`Error compiling ${name} Component: ${result.error}`);
    }
    if (mgr && comp) {
      return { mgr, comp, ic };
    }
  } catch (e) {
    console.error(e);
    platform.showError(`Error compiling ${name}: ${getErrorMessage(e)}`);
    return null;
  }
  return {
    ic,
    mgr: unknownManager(name as ComponentItemType),
    comp: UnknownComponent,
  };
}

function register(cic: CompiledInstalledComponent): void {
  const { ic, mgr, comp } = cic;
  const { name } = ic;
  const componentTypeName = upperFirst(camelCase(name));
  registerManager(componentTypeName, mgr);
  registerParentItemTypes(componentTypeName, mgr);
  // @ts-ignore dynamic name
  registerComponent(componentTypeName, comp, true);
}

function loadInstalledComponents(platform: Platform, appUid: string): void {
  deregisterCustomComponents();
  const installedComponents = getInstalledComponents(appUid);
  installedComponents?.forEach(register);
}

function getInstalledComponents(appUid: string): CompiledInstalledComponent[] {
  return InstalledComponentsMap.value[appUid] ?? [];
}

function setInstalledComponents(
  appUid: string,
  value: CompiledInstalledComponent[],
): void {
  InstalledComponentsMap.value[appUid] = value;
}

function replaceInstalledComponent(
  platform: Platform,
  appUid: string,
  cic: CompiledInstalledComponent,
): void {
  let ics = getInstalledComponents(appUid);
  ics = ics.filter(
    ({ ic: { componentUid } }) => cic.ic.componentUid !== componentUid,
  );
  ics.push(cic);
  setInstalledComponents(appUid, ics);
}

function isInstalled(
  appUid: string,
  p_componentId: string,
  p_version: string,
): boolean {
  return !!getInstalledComponents(appUid).find(
    ({ ic: { componentUid, version } }) =>
      p_componentId === componentUid && version === p_version,
  );
}

function getInstalledVersion(appUid: string, p_componentId: string): string {
  const cic = getInstalledComponents(appUid).find(
    ({ ic: { componentUid } }) => p_componentId === componentUid,
  );
  return cic ? cic.ic.version : '';
}

async function install(
  platform: Platform,
  appUid: string,
  sourceId: string,
): Promise<boolean> {
  const store = platform.getStore<dsTypes.IoInstalledComponentsV>(
    appUid,
    '$$Sidebar$$',
    'IoInstalledComponentsV',
  );
  if (!store) {
    throw Error('Missing store IoInstalledComponentsV');
  }
  const records = await store.query({
    props: {
      filter: [{ sourceUid: { is: sourceId } }],
      // force the fetch the latest updated source
      force: true,
    },
  });

  if (records && Object.values(records).length) {
    const ic = Object.values(records)[0];
    const cic = await compile(platform, ic as dsTypes.IoInstalledComponentsV);
    if (cic) {
      replaceInstalledComponent(platform, appUid, cic);
      loadInstalledComponents(platform, appUid);
      platform.setter(hmrAtom, (counter = 0) => counter + 1);
      return true;
    }
  } else {
    platform.showError(`Missing component source ${appUid}.${sourceId}!`);
  }
  return false;
}

export {
  compile,
  getInstalledComponents,
  getInstalledVersion,
  install,
  isInstalled,
  loadInstalledComponents,
  setInstalledComponents,
};
